import { MetricsChart } from 'components/MetricsChart';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { ChartData } from 'components/chart/data/ChartData';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { LineChartConfig } from 'components/chart/data/LineChartConfig';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { useIntl } from 'react-intl';
import { useDatePickerLabels } from 'hooks/useDatePickerLabels';
import { OverTimeReportInfo, TimeInfo } from 'pages/insights-pro/common/reports/over-time-chart/models';
import { Measure } from 'services/model/model.service';

interface CountryOverTimeChartProps {
  overTimeReport: OverTimeReportInfo;
  timeInfos: TimeInfo[];
  measure: Measure;
  maxGmv?: number;
  maxShare?: number;
}

export function OverTimeChart({
  overTimeReport,
  timeInfos,
  measure,
  maxGmv = undefined,
  maxShare = undefined,
}: CountryOverTimeChartProps) {
  const intl = useIntl();
  const dateRangeLabelContext = useDatePickerLabels();

  const createChartConfig = () => {
    const chartConfig = new LineChartConfig();
    chartConfig.value = {
      code: measure.code,
      label: measure.metric.name,
      unit: measure.metric.unit,
      max: maxGmv,
    };
    chartConfig.additionalAxis = true;
    chartConfig.additionalValue = {
      code: 'share',
      label: 'Share',
      unit: MeasureUnitCode.PERCENT,
      max: maxShare,
    };
    chartConfig.legendConfig = {
      hide: false,
    };
    chartConfig.data = [
      new ChartDataArray(
        ChartDataType.primary,
        dateRangeLabelContext.translatedSafeDateRangeLabel(intl),
        overTimeReport.timeInfos.map(entry => new ChartData(entry.code, entry.gmv))
      ),
      new ChartDataArray(
        ChartDataType.primary,
        dateRangeLabelContext.translatedSafeComparisonRangeLabel(intl),
        overTimeReport.timeInfos.map(entry => new ChartData(entry.code, entry.comparisonGmv ?? 0)),
        ChartDataComparisonType.comparison
      ),
      new ChartDataArray(
        ChartDataType.secondary,
        'Share',
        overTimeReport.timeInfos.map(
          entry => new ChartData(entry.code, entry.gmv / (timeInfos.find(ti => ti.code === entry.code)?.gmv ?? 0))
        )
      ),
      new ChartDataArray(
        ChartDataType.secondary,
        'Share Comparison',
        overTimeReport.timeInfos.map(
          entry =>
            new ChartData(
              entry.code,
              (entry.comparisonGmv ?? 0) / (timeInfos.find(ti => ti.code === entry.code)?.comparisonGmv ?? 0)
            )
        ),
        ChartDataComparisonType.comparison
      ),
    ];

    return chartConfig;
  };

  const chartRenderer = new LineChartRenderer(createChartConfig());

  return <MetricsChart renderer={chartRenderer} height="h-[256px]" />;
}
